import type { DecouverteProfile } from './decouverte-profile';

/**
 * Contenu de l'accueil pré-login `/decouverte`. Données pures (pas de JSX) : les
 * visuels de preuve vivent dans `decouverte-proofs.tsx`, la mise en page dans
 * `decouverte-carousel.tsx`.
 *
 * Parcours : un écran d'accueil (photo + promesse + « Tu es… » avec quatre VRAIS
 * boutons), puis 3 à 4 diapositives propres au profil choisi. Chaque diapositive
 * = une promesse (titre + surlignage) + une preuve visuelle. On ne montre pas
 * tout : seulement ce qui fait signer CE profil.
 *
 * Photos : `apps/app/public/decouverte/*.webp` — celles du Hero du site
 * (`landing_hero_phases`) et les couvertures Live School (`live-covers/presets`),
 * recadrées et compressées pour tenir dans l'app (≈ 400 Ko les 8), lisibles hors
 * ligne. Garder la copie alignée avec le Hero du site (`apps/marketing`).
 */

export interface DecouverteListRow {
  icon: string;
  label: string;
  detail?: string;
  tone: 'good' | 'warn' | 'info' | 'gold';
}

export interface DecouverteYearStop {
  month: string;
  title: string;
  state: 'done' | 'now' | 'next' | 'goal';
}

export type DecouverteProof =
  | { kind: 'sheet' }
  | { kind: 'cards'; chip?: string }
  | { kind: 'live' }
  | { kind: 'year'; badge: string; stops: readonly DecouverteYearStop[] }
  | { kind: 'list'; title: string; rows: readonly DecouverteListRow[] };

export interface DecouverteSlide {
  id: string;
  /** Chemin public de la photo (`/decouverte/…webp`). */
  image: string;
  /** Cadrage de la photo sur un téléphone en portrait (classe Tailwind `object-[x_y]`). */
  imageFocus: string;
  eyebrow: string;
  title: string;
  /** Fin du titre, mise en couleur. */
  highlight: string;
  body: string;
  proof: DecouverteProof;
}

export interface DecouverteTrack {
  profile: DecouverteProfile;
  /** Bouton de l'accueil : « Élève ». */
  label: string;
  /** Précision sous le libellé du bouton. */
  hint: string;
  emoji: string;
  slides: readonly DecouverteSlide[];
  /** CTA principal de la dernière diapositive (→ inscription). */
  cta: string;
  /** Lien secondaire → démo sans compte. */
  demoLabel: string;
}

export const DECOUVERTE_HERO = {
  image: '/decouverte/soir.webp',
  imageFocus: 'object-[50%_40%]',
  eyebrow: 'Maths · Physique-chimie · Collège → Prépa',
  title: 'Apprendre à raisonner.',
  highlight: 'Pas à recopier.',
  body: 'Des exercices pas à pas, un brouillon qui vérifie chaque ligne, des cartes à faire monter.',
  question: 'Tu es…',
} as const;

/** Programme type de 3e (packs publiés de `curriculum_packs`, maths 3e). */
const YEAR_3E: readonly DecouverteYearStop[] = [
  { month: 'Sept', title: 'Remise en route', state: 'done' },
  { month: 'Nov', title: 'Calcul littéral', state: 'now' },
  { month: 'Janv', title: 'Pythagore et Thalès', state: 'next' },
  { month: 'Juin', title: 'Objectif brevet', state: 'goal' },
];

const STUDENT: DecouverteTrack = {
  profile: 'student',
  label: 'Élève',
  hint: 'Collège, lycée ou prépa',
  emoji: '🎒',
  cta: 'Créer mon compte gratuit',
  demoLabel: 'Essayer sans compte',
  slides: [
    {
      id: 'pas-a-pas',
      image: '/decouverte/bureau.webp',
      imageFocus: 'object-[62%_40%]',
      eyebrow: 'Pas à pas',
      title: 'Bloqué ?',
      highlight: 'Avance ligne par ligne.',
      body: 'Écris ton calcul sur la feuille de brouillon : chaque ligne est vérifiée tout de suite. Une erreur ? Tu vois où, et pourquoi.',
      proof: { kind: 'sheet' },
    },
    {
      id: 'skillcards',
      image: '/decouverte/parc.webp',
      imageFocus: 'object-[58%_35%]',
      eyebrow: 'SKILLCARDS · HORA',
      title: 'Chaque méthode',
      highlight: 'devient une carte.',
      body: 'Chaque carte a sa leçon et ses exercices. Une bonne réponse fait monter ses HORA ; si tu ne révises plus, ils redescendent.',
      proof: { kind: 'cards', chip: '+40 HORA' },
    },
    {
      id: 'live-school',
      image: '/decouverte/arene.webp',
      imageFocus: 'object-[50%_45%]',
      eyebrow: 'Live School',
      title: 'Chaque dimanche,',
      highlight: 'un défi en direct.',
      body: 'Les mêmes questions pour tous les élèves de ton niveau, un classement en direct, une SKILLCARD légendaire à gagner. Et tes propres défis entre amis.',
      proof: { kind: 'live' },
    },
    {
      id: 'diagnostic',
      image: '/decouverte/lycee.webp',
      imageFocus: 'object-[40%_40%]',
      eyebrow: 'Diagnostic · Programme',
      title: '5 minutes pour savoir',
      highlight: 'où tu en es.',
      body: 'Un diagnostic gratuit d’une quinzaine de questions, puis ton programme de l’année, pack par pack, mois par mois.',
      proof: { kind: 'year', badge: 'Diagnostic gratuit · 5 min', stops: YEAR_3E },
    },
  ],
};

const PARENT: DecouverteTrack = {
  profile: 'parent',
  label: 'Parent',
  hint: 'Suivre les progrès de mon enfant',
  emoji: '👨‍👩‍👧',
  cta: 'Créer mon espace parent',
  demoLabel: 'Voir un exercice',
  slides: [
    {
      id: 'diagnostic',
      image: '/decouverte/lycee.webp',
      imageFocus: 'object-[40%_40%]',
      eyebrow: 'Diagnostic gratuit',
      title: 'Sache enfin',
      highlight: 'où en est ton enfant.',
      body: 'En 5 minutes, il passe le diagnostic de son programme. Tu reçois un bilan clair, carte par carte : ce qui est acquis, ce qui reste à retravailler.',
      proof: {
        kind: 'list',
        title: 'Bilan · Maths 3e',
        rows: [
          { icon: '📐', label: 'Pythagore', detail: 'Acquis', tone: 'good' },
          { icon: '✏️', label: 'Calcul littéral', detail: 'En cours', tone: 'info' },
          { icon: '➗', label: 'Fractions', detail: 'À retravailler', tone: 'warn' },
        ],
      },
    },
    {
      id: 'programme',
      image: '/decouverte/bureau.webp',
      imageFocus: 'object-[62%_40%]',
      eyebrow: 'Le programme de l’année',
      title: 'Tout le programme,',
      highlight: 'mois par mois.',
      body: 'Chaque pack rassemble les SKILLCARDS du mois : leçon, méthode, exercices pas à pas. Une alternative aux cours particuliers, à son rythme.',
      proof: { kind: 'year', badge: 'Packs de SKILLCARDS', stops: YEAR_3E },
    },
    {
      id: 'suivi',
      image: '/decouverte/parc.webp',
      imageFocus: 'object-[58%_35%]',
      eyebrow: 'Suivi',
      title: 'Suis ses progrès',
      highlight: 'sans être sur son dos.',
      body: 'Tu vois son activité et ses HORA monter, carte par carte. Et il apprend à raisonner : jamais de corrigé à recopier.',
      proof: {
        kind: 'list',
        title: 'Cette semaine',
        rows: [
          { icon: '📈', label: '+120 HORA', detail: '4 séances', tone: 'good' },
          { icon: '🃏', label: 'Pythagore', detail: 'Carte au MAX', tone: 'gold' },
          { icon: '🔔', label: 'Diagnostic', detail: '2 cartes à revoir', tone: 'info' },
        ],
      },
    },
    {
      // « Joue avec lui/elle » (29/10/2026) : le parent qui s'implique, par le jeu — défi live privé
      // (code ou QR, chacun son téléphone, classement en direct, « Prendre ma revanche »).
      id: 'joue-avec',
      image: '/decouverte/arene.webp',
      imageFocus: 'object-[50%_50%]',
      eyebrow: 'Joue avec lui/elle',
      title: 'Les Maths, ce n’est pas',
      highlight: 'qu’un jeu d’enfant !',
      body: 'Ce soir, défi en famille : un code, chacun son téléphone, le classement en direct. Il gagne ? Demande-lui de t’expliquer : enseigner, c’est apprendre deux fois. Et te voir chercher, te tromper, recommencer lui apprend que les maths, ça se travaille.',
      proof: {
        kind: 'list',
        title: 'Ce soir, à la maison',
        rows: [
          { icon: '🏆', label: 'Défi en famille', detail: 'Léa 4 · toi 3', tone: 'gold' },
          { icon: '🗣️', label: 'Léa t’explique', detail: 'les fractions', tone: 'info' },
          { icon: '🔁', label: 'Ta revanche', detail: 'demain, 19 h', tone: 'good' },
        ],
      },
    },
  ],
};

const TUTOR: DecouverteTrack = {
  profile: 'tutor',
  label: 'Prof',
  hint: 'Cours particuliers, soutien, classe',
  emoji: '🧑‍🏫',
  cta: 'Essayer 7 jours gratuits',
  demoLabel: 'Voir un exercice',
  slides: [
    {
      id: 'cartes',
      image: '/decouverte/classe.webp',
      imageFocus: 'object-[55%_50%]',
      eyebrow: 'SKILLCARDS',
      title: 'Envoie la bonne carte',
      highlight: 'au bon élève.',
      body: 'Plus de 700 SKILLCARDS de la 6e à la prépa, en maths et en physique-chimie : leçon, méthode et exercices pas à pas, prêts à assigner.',
      proof: { kind: 'cards', chip: 'Envoyée · Groupe 3e' },
    },
    {
      id: 'suivi',
      image: '/decouverte/cahier.webp',
      imageFocus: 'object-[60%_50%]',
      eyebrow: 'Suivi',
      title: 'Suis chaque élève,',
      highlight: 'carte par carte.',
      body: 'Diagnostic, HORA, exercices faits : tu vois où chacun en est et tu es prévenu de son activité. Le brouillon vérifie chaque ligne à ta place.',
      proof: {
        kind: 'list',
        title: 'Groupe 3e',
        rows: [
          { icon: '🧑', label: 'Inès', detail: 'Thalès · à retravailler', tone: 'warn' },
          { icon: '🧑', label: 'Hugo', detail: 'Pythagore · au MAX', tone: 'gold' },
          { icon: '🧑', label: 'Nora', detail: 'Diagnostic terminé', tone: 'good' },
        ],
      },
    },
    {
      id: 'espace-prof',
      image: '/decouverte/salle.webp',
      imageFocus: 'object-[50%_50%]',
      eyebrow: 'Ton espace prof',
      title: 'Tes cours,',
      highlight: 'au même endroit.',
      body: 'Visio, créneaux réservables, paiements, documents et discussions. Et des défis en direct pour tes groupes.',
      proof: {
        kind: 'list',
        title: 'Cette semaine',
        rows: [
          { icon: '🎥', label: 'Mar 18:00', detail: 'Visio · Hugo', tone: 'info' },
          { icon: '📅', label: 'Mer 14:00', detail: 'Réservé · Inès', tone: 'good' },
          { icon: '🏆', label: 'Dim 17:00', detail: 'Défi · Groupe 3e', tone: 'gold' },
        ],
      },
    },
  ],
};

const PLAYER: DecouverteTrack = {
  profile: 'player',
  label: 'Curieux',
  hint: 'Me remettre aux maths, par plaisir',
  emoji: '🧠',
  cta: 'Créer mon compte gratuit',
  demoLabel: 'Essayer sans compte',
  slides: [
    {
      id: 'bilan',
      image: '/decouverte/cahier.webp',
      imageFocus: 'object-[60%_50%]',
      eyebrow: 'Bilan gratuit',
      title: 'Et toi, tu en es où',
      highlight: 'en maths ?',
      body: 'Choisis une classe, de la 6e à la Terminale, et réponds à 15 questions quand tu veux. Ton bilan te dit ce que tu maîtrises encore et ce que tu peux revoir.',
      proof: {
        kind: 'list',
        title: 'Mon bilan · niveau 3e',
        rows: [
          { icon: '📐', label: 'Pythagore', detail: 'Toujours là', tone: 'good' },
          { icon: '➗', label: 'Fractions', detail: 'À dépoussiérer', tone: 'warn' },
          { icon: '✏️', label: 'Calcul littéral', detail: 'À revoir', tone: 'info' },
        ],
      },
    },
    {
      id: 'pas-a-pas',
      image: '/decouverte/bureau.webp',
      imageFocus: 'object-[62%_40%]',
      eyebrow: 'Pas à pas',
      title: 'Refais des maths,',
      highlight: 'ligne par ligne.',
      body: 'Écris ton calcul sur la feuille de brouillon : chaque ligne est vérifiée tout de suite. Une erreur ? Tu vois où, et pourquoi.',
      proof: { kind: 'sheet' },
    },
    {
      id: 'defis',
      image: '/decouverte/arene.webp',
      imageFocus: 'object-[50%_45%]',
      eyebrow: 'Défis en direct',
      title: 'Lance un défi',
      highlight: 'à tes proches.',
      body: 'Quelques questions, un code à partager : chacun joue sur son téléphone, classement en direct. Et les défis du dimanche, au niveau que tu veux.',
      proof: { kind: 'live' },
    },
  ],
};

export const DECOUVERTE_TRACKS: Readonly<Record<DecouverteProfile, DecouverteTrack>> = {
  student: STUDENT,
  parent: PARENT,
  tutor: TUTOR,
  player: PLAYER,
};

/** Ordre des boutons de l'accueil. */
export const DECOUVERTE_TRACK_ORDER: readonly DecouverteProfile[] = [
  'student',
  'parent',
  'tutor',
  'player',
];

/** Toutes les photos du parcours (préchargement). PUR. */
export function decouverteImages(): string[] {
  const all = [
    DECOUVERTE_HERO.image,
    ...Object.values(DECOUVERTE_TRACKS).flatMap((t) => t.slides.map((s) => s.image)),
  ];
  return [...new Set(all)];
}
